export type SiteId = 'core' | 'engine' | 'hardware' | 'education';

const OVERRIDE_KEY = 'oneill.site-override';

const SUBDOMAIN_TO_SITE: Record<string, SiteId> = {
    engine: 'engine',
    hardware: 'hardware',
    education: 'education',
};

const SITE_ORIGIN: Record<SiteId, string> = {
    core: 'https://oneillsoft.com',
    engine: 'https://engine.oneillsoft.com',
    hardware: 'https://hardware.oneillsoft.com',
    education: 'https://education.oneillsoft.com',
};

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

const isSiteId = (value: string | null): value is SiteId =>
    value !== null && value in SITE_ORIGIN;

// sessionStorage throws in some privacy modes; a failure here must not take down the app.
const readOverride = (): string | null => {
    try {
        return window.sessionStorage.getItem(OVERRIDE_KEY);
    } catch {
        return null;
    }
};

const writeOverride = (value: string): void => {
    try {
        window.sessionStorage.setItem(OVERRIDE_KEY, value);
    } catch {
        /* ignore */
    }
};

/**
 * Picks the site to render from the hostname, e.g. engine.oneillsoft.com.
 * `?site=engine` forces a site locally and persists for the rest of the tab session.
 */
export const resolveSite = (): SiteId => {
    const requested = new URLSearchParams(window.location.search).get('site');

    if (isSiteId(requested)) {
        writeOverride(requested);
        return requested;
    }

    const stored = readOverride();

    if (isSiteId(stored)) {
        return stored;
    }

    const subdomain = window.location.hostname.split('.')[0];

    return SUBDOMAIN_TO_SITE[subdomain] ?? 'core';
};

const localSubdomainUrl = (site: SiteId, path: string): string => {
    const { protocol, port } = window.location;
    const host = site === 'core' ? 'localhost' : `${site}.localhost`;
    return `${protocol}//${host}${port ? `:${port}` : ''}${path}`;
};

/** Link target for another site — the real subdomain in production, a `?site=` link locally. */
export const siteUrl = (site: SiteId, path = '/'): string => {
    const { hostname } = window.location;
    if (LOCAL_HOSTS.includes(hostname)) {
        return `${window.location.origin}${path}?site=${site}`;
    }
    if (hostname.endsWith('.localhost')) {
        return localSubdomainUrl(site, path);
    }
    return `${SITE_ORIGIN[site]}${path === '/' ? '' : path}`;
};
